import { IconSearch } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useSearchSuggestionsQuery } from "../hooks/useSearchSuggestionsQuery";

export function SearchSuggestions({
  searchValue,
  onSelectRefine,
}: {
  searchValue: string;
  /** Called when the user picks a refine query. Caller pushes to history and re-runs search. */
  onSelectRefine: (query: string) => void;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const { refineQueries, isLoading } = useSearchSuggestionsQuery(searchValue);

  if (isLoading) return <SuggestionsSkeleton />;
  if (refineQueries.length === 0) return null;

  return (
    <div className="flex w-full flex-col gap-1">
      <div className="flex items-center gap-1.5 px-4 py-1">
        <IconSearch size={14} className="text-ds-slate-400" />
        <p className="text-xs font-semibold uppercase tracking-wide text-ds-slate-400">
          {t("Search articles")}
        </p>
      </div>
      <ul className="flex w-full flex-col">
        {refineQueries.map((q) => (
          <SuggestionRow
            key={q}
            title={q}
            subtitle={t("Browse articles")}
            onClick={() => {
              onSelectRefine(q);
            }}
          />
        ))}
      </ul>
    </div>
  );
}

function SuggestionRow({
  title,
  subtitle,
  onClick,
}: {
  title: string;
  subtitle: string;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <li className="animate-fade-in">
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center gap-3 rounded-xl p-2  text-left transition-colors hover:bg-ds-slate-50"
      >
        <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-ds-mint-100">
          <IconSearch size={20} className="text-ds-mint-700" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="truncate text-sm font-semibold text-ds-black">
            {title}
          </span>
          <span className="truncate text-xs text-ds-slate-500">{subtitle}</span>
        </span>
      </button>
    </li>
  );
}

function SuggestionsSkeleton(): React.JSX.Element {
  return (
    <div className="flex w-full flex-col gap-2">
      <div className="h-5 w-32 animate-pulse rounded bg-ds-slate-100" />
      <div className="h-14 w-full animate-pulse rounded-xl bg-ds-slate-100" />
      <div className="h-14 w-full animate-pulse rounded-xl bg-ds-slate-100" />
    </div>
  );
}
